/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/alt-text */
/* eslint-disable no-undef */
/* eslint no-console: ["error", { allow: ["error"] }] */
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  memo,
  useDeferredValue,
  lazy,
  Suspense,
  startTransition
} from 'react'

// Wagmi / EVM
import {
  readContract,
  readContracts,
  waitForTransactionReceipt,
  writeContract
} from '@wagmi/core'
import { useAccount, useSwitchChain, useChainId } from 'wagmi'

// Router
import { Link } from 'react-router-dom'

// Third‑party libs
import Select from 'react-select'
import Cookies from 'universal-cookie'
import Web3 from 'web3'
import { toast } from 'react-hot-toast'
import { useQueryParam, StringParam } from 'use-query-params'

// Styles
import '../App.css'
import '../styles/MainContainer.css'

// Utils & constants
import { getAbi } from '../utils/pickerHelperABI.ts'
import formatNumber, {
  imageUrl,
  ethPriceApiUrl,
  registryChainIds,
  chainLogos,
  apiUrl,
  coinNames,
  web3Clients
} from '../utils/constants.ts'
import { getDefaultAddress, getAllMulticallAddresses } from '../utils/addressHelpers.ts'
import rot13 from '../../utils/encode.ts'

// Live buzz store
import {
  subscribeLiveBuzz,
  getRecentMap,
  pruneExpired,
  getLastBumpMap
} from '../state/liveBuzzStore'

// Config / ABIs & adapters
import { wagmiAdapter } from '../App.jsx'
import InfoAbiV2 from '../config/InfoAbiV2.json'
import { fetchDevAddress } from '../utils/pumpMetadata.ts'
import { calculateBondingProgress } from '../utils/bondingCurve.ts'

// Components
import TopBar from '../components/TopBar'
import LaunchpadCard from '../components/LaunchpadCard'
import RevenueHeader from '../components/RevenueHeader'
import TopTenList from '../components/TopTenList'
import GraduatedCoins from '../components/GraduatedCoins'
import SearchSection from '../components/SearchSection'

const Slider = lazy(() => import('../components/Slider.jsx'))
const Footer = lazy(() => import('../components/Footer'))

// ---- watchlist helpers (step 1) ----
const wlKey = (address, chainId) => {
  const addr = String(address || '').toLowerCase()
  const c = chainId ?? ''
  return c === '' ? addr : `${addr}:${c}`
}
const isWatched = (wl, address, chainId) => {
  const addr = String(address || '').toLowerCase()
  if (!Array.isArray(wl) || !addr) return false
  const key = wlKey(addr, chainId)
  return wl.includes(key) || wl.includes(addr)
}

const MemoLaunchpadCard = memo(LaunchpadCard, (prev, next) => {
  const keys = [
    'buyAmount',
    'startTime',
    'chainId',
    'progress',
    'liquidity',
    'tokenName',
    'tokenSymbol',
    'tokenDescription',
    'bannerUrl',
    'chadAddress',
    'depositedAmount',
    'contractAddress',
    'tokenAddress',
    'devAddress',
    'dexName',
    'marketCap',
    'website',
    'twitter',
    'telegram',
    'lpCreated',
    'lastAction',
    'lastActionNumber',
    'top15HoldersPercent',
    'devPercent',
    'holdersNumber',
    'tokenId',
    'logoUrl',
    'blockchainLogoUrl',
    'versionType',
    'canCreate'
    // 'LogoUrl',
    // 'BlockchainLogoUrl'
  ]
  for (const k of keys) {
    if (prev[k] !== next[k]) return false
  }
  // watchlist membership matters
  const prevFav = isWatched(prev.watchlist, prev.contractAddress, prev.chainId)
  const nextFav = isWatched(next.watchlist, next.contractAddress, next.chainId)
  if (prevFav !== nextFav) return false

  // 🔔 Important: allow re-render when the live-bump glow state changes
  if (prev.recentBuzz !== next.recentBuzz) return false

  return true
})

// Money: K/M/B with **2 fixed decimals** (module-scope)

const formatMoney1 = n => {
  const x = Number(n) || 0
  const abs = Math.abs(x)
  let v = x
  let s = ''
  if (abs >= 1e9) {
    v = x / 1e9
    s = 'B'
  } else if (abs >= 1e6) {
    v = x / 1e6
    s = 'M'
  } else if (abs >= 1e3) {
    v = x / 1e3
    s = 'K'
  }
  const str = Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })
  return str + s
}

// —— Live-bump (buy -> bump to top + glow) infrastructure ——
// How long a card stays “hot” after a buy (longer so users can actually see the glow)
const BUY_BUMP_TTL_MS = 8000
// (optional) Poll cadence if we ever fall back to polling instead of the live store
const BUY_POLL_MS = 10000
/**
 * Stable key for buy events across sources; normalize fields and casing to
 * avoid duplicate/missed bumps when contract/buyer casing differs.
 */
const buyEventKey = it => {
  const chain = Number(it?.chainId ?? it?.chain) || it?.chainId || ''
  const contract = String(
    it?.contract ?? it?.contractAddress ?? ''
  ).toLowerCase()
  const buyer = String(it?.buyer ?? '').toLowerCase()
  // stringify amount to avoid float precision mismatches in keys
  const amount = String(it?.amount ?? it?.value ?? '')
  return `${chain}:${contract}:${buyer}:${amount}`
}

// ---- lightweight cache & concurrency helpers ----
const cacheGet = key => {
  try {
    const raw = sessionStorage.getItem(key)
    if (!raw) return null
    const { v, e } = JSON.parse(raw)
    if (e && Date.now() > e) {
      sessionStorage.removeItem(key)
      return null
    }
    return v
  } catch {
    return null
  }
}
const cacheSet = (key, value, ttlSec = 90) => {
  try {
    sessionStorage.setItem(
      key,
      JSON.stringify({ v: value, e: Date.now() + ttlSec * 1000 })
    )
  } catch {}
}
const mkLimiter = (limit = 5) => {
  let active = 0
  const q = []
  const runNext = () => {
    const fn = q.shift()
    if (fn) fn()
  }
  return async fn => {
    if (active >= limit) await new Promise(res => q.push(res))
    active++
    try {
      return await fn()
    } finally {
      active--
      runNext()
    }
  }
}

// --- timeouts & fast-fallback helpers ---
const promiseWithTimeout = (label, promiseFactory, ms = 8000) => {
  let timer
  return new Promise((resolve, reject) => {
    timer = setTimeout(() => {
      const err = new Error(`Timeout after ${ms}ms: ${label}`)
      err.name = 'TimeoutError'
      reject(err)
    }, ms)
    Promise.resolve()
      .then(promiseFactory)
      .then(res => resolve(res))
      .catch(err => reject(err))
      .finally(() => clearTimeout(timer))
  })
}

const fetchWithTimeout = async (url, opts = {}, ms = 8000) => {
  const { signal: outerSignal, ...rest } = opts || {}
  const ac = new AbortController()
  const onAbort = () => ac.abort()
  if (outerSignal) {
    if (outerSignal.aborted) ac.abort()
    else outerSignal.addEventListener('abort', onAbort, { once: true })
  }
  const t = setTimeout(() => ac.abort(), ms)
  try {
    return await fetch(url, { ...rest, signal: ac.signal })
  } finally {
    clearTimeout(t)
    if (outerSignal) outerSignal.removeEventListener('abort', onAbort)
  }
}

// Try a call once; on timeout, retry one more time quickly.
const callWithTimeoutAndRetry = async (
  label,
  fn,
  ms = 8000,
  retryMs = 5000
) => {
  try {
    return await promiseWithTimeout(label, fn, ms)
  } catch (e) {
    if (e?.name === 'TimeoutError') {
      warn(`⏳ ${label} timed out (${ms}ms). Retrying once...`)
      return await promiseWithTimeout(`${label} (retry)`, fn, retryMs)
    }
    throw e
  }
}

// Cooperative yield to keep the main thread responsive during long loops
const microYield = () => new Promise(res => setTimeout(res, 0))

// SWR-style latest chats (session cached ~90s, background refresh)
const useLatestChatsSWR = () => {
  const [latestChats, setLatestChats] = useState([])
  useEffect(() => {
    const key = 'latestChats'
    const cached = cacheGet(key)
    if (cached) setLatestChats(cached)
    let cancelled = false
    const refresh = async () => {
      try {
        const res = await fetch(apiUrl + '/api/getLatestChats', {
          method: 'GET'
        })
        const data = await res.json()
        if (!cancelled) {
          setLatestChats(Array.isArray(data) ? data : [])
          cacheSet(key, Array.isArray(data) ? data : [], 90) // 90s TTL
        }
      } catch {}
    }
    // always refresh in the background
    setTimeout(refresh, 0)
    return () => {
      cancelled = true
    }
  }, [])
  return latestChats
}

// -------------------------------------------------
// Dev-only logging helpers

const DEV = process.env.NODE_ENV !== 'production'
const log = (...args) => {
  if (DEV) {
    /* eslint-disable no-console */
    console.log(...args)
    /* eslint-enable no-console */
  }
}
const warn = (...args) => {
  if (DEV) {
    /* eslint-disable no-console */
    console.warn(...args)
    /* eslint-enable no-console */
  }
}
// In production, silence stray console logs/warns just in case
if (!DEV) {
  /* eslint-disable no-console */
  const noop = () => {}
  try {
    console.log = noop
    console.warn = noop
    console.info = noop
    console.debug = noop
  } catch {}
  /* eslint-enable no-console */
}

// Window width hook for responsive assets (SSR-safe)
const useWindowWidth = () => {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 1024
  )
  useEffect(() => {
    const onResize = () => setW(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])
  return w
}

const App = () => {
  const [showMobileFilters, setShowMobileFilters] = useState(false)
  const [loadingEssential, setLoadingEssential] = useState(false)
  const [loadingDeferred, setLoadingDeferred] = useState(false)
  const [chadLists, setChadListData] = useState([])
  const [accumFee, setAccumFee] = useState()
  const [accumFeeUnavailable, setAccumFeeUnavailable] = useState(false)
  const [essentialData, setEssentialData] = useState(null)
  const cookies = new Cookies()
  const [modalIsOpen, setModalIsOpen] = useState(false)
  const [search, setSearch] = useState('')
  // Debounced search input (user types here; we apply to `search` after a short delay)
  const [searchInput, setSearchInput] = useState('')
  const { switchChain, error: switchError } = useSwitchChain()
  const currentChainId = useChainId() // Get the current connected chain

  // Abort controller for Phase 2 async work (fees/histories/holders)
  const phase2AbortRef = useRef(null)
  // Abort controller for Phase-1 essentials
  const phase1AbortRef = useRef(null)

  // Phase-1 streaming coordination for Phase-2
  const phase1StreamingRef = useRef(false) // true while Phase-1 streams per-chain
  const phase2HasRunDuringStreamRef = useRef(false) // ensure only one Phase-2 during stream
  const lastPhase2ProcessedSizeRef = useRef(0) // remember last full size processed by Phase-2

  // Debounce: apply `searchInput` to `search` after 200ms of inactivity
  useEffect(() => {
    const t = setTimeout(
      () => startTransition(() => setSearch(searchInput)),
      200
    )
    return () => clearTimeout(t)
  }, [searchInput])
  const [buyAmount, setBuyAmount] = useState('1')
  const [etherPrice, setEtherPrice] = useState()
  const buyPerChain = useMemo(() => {
    const out = {}
    if (!etherPrice) return out
    try {
      for (const id of registryChainIds) {
        const p = etherPrice[id]
        out[id] = p ? Number(buyAmount) / p : 0
      }
    } catch {}
    return out
  }, [buyAmount, etherPrice])
  const [poolAddress, setPoolAddress] = useState('')
  const [creating1, setCreating1] = useState(false)
  const [watchlist, setWatchlist] = useState([])
  const [selectedItem, setSelectedItem] = useState('1W')
  const [showTop10, setShowTop10] = useState(true)
  // Live-bump toggle + state
  const [liveBuzzEnabled, setLiveBuzzEnabled] = useState(true)
  const recentBuysRef = useRef(new Map()) // contractAddress -> expireTs
  const lastBumpRef = useRef(new Map()) // address -> lastBumpTs (persistent ordering)
  const [, forceBumpTick] = useState(0) // re-render when expirations prune
  useEffect(() => {
    let cancelled = false
    // seed from shared store
    try {
      const seed = getRecentMap(currentChainId)
      if (seed && seed.size) {
        recentBuysRef.current = new Map(seed)
      }
      try {
        const lb = getLastBumpMap(currentChainId)
        if (lb && lb.size) {
          lastBumpRef.current = new Map(lb)
        }
      } catch {}
      forceBumpTick(v => v + 1)
    } catch {}

    // subscribe to future updates from TopBar
    const unsub = subscribeLiveBuzz(cid => {
      if (cancelled || Number(cid) !== Number(currentChainId)) return
      const m = getRecentMap(cid)
      recentBuysRef.current = new Map(m)
      try {
        const lb = getLastBumpMap(cid)
        lastBumpRef.current = new Map(lb)
      } catch {}
      forceBumpTick(v => v + 1)
    })

    // local prune timer keeps UI fresh even if no new events
    const pruneId = setInterval(() => {
      try {
        pruneExpired(currentChainId)
      } catch {}
    }, 1000)

    return () => {
      cancelled = true
      unsub && unsub()
      clearInterval(pruneId)
    }
  }, [currentChainId])

  // Derive a plain-address watchlist for LaunchpadCard.
  const plainWatchlist = useMemo(() => {
    return Array.isArray(watchlist)
      ? watchlist.map(item => String(item).split(':')[0])
      : []
  }, [watchlist])
  // Track whether Phase-2 "final" pass has completed (for UI gating if needed)
  const [phase2FinalDone, setPhase2FinalDone] = useState(false)
  const [firstPaintDone, setFirstPaintDone] = useState(false)
  // Graduated section skeleton latch
  const [graduatedPaintDone, setGraduatedPaintDone] = useState(false)
  // Graduated aside: ensure skeleton shows for a minimum time to avoid flash
  const [gradSkelHoldDone, setGradSkelHoldDone] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setGradSkelHoldDone(true), 450)
    return () => clearTimeout(t)
  }, [])

  // Helper for safe BigInt -> JS number conversion, with decimal formatting
  const formatUnitsNum = (value, decimals = 18) => {
    try {
      const bi = BigInt(value ?? 0n)
      const s = bi.toString()
      if (decimals === 0) return Number(s)
      const pad = Number(decimals)
      if (s.length <= pad) {
        return Number(`0.${'0'.repeat(pad - s.length)}${s}`)
      }
      const int = s.slice(0, s.length - pad)
      const frac = s.slice(s.length - pad, s.length) // keep full precision then JS rounds
      return Number(`${int}.${frac}`)
    } catch {
      return 0
    }
  }
  // Only log hotcurves count if it changed (DEV only)
  const chadCountRef = useRef(-1)
  const __count = Array.isArray(chadLists) ? chadLists.length : 0
  if (DEV && chadCountRef.current !== __count) {
    log('[board] hotcurves count =', __count)
    chadCountRef.current = __count
  }

  let [ref] = useQueryParam('ref', StringParam)
  const { address } = useAccount()

  useEffect(() => {
    if (!ref) return
    try {
      if (Web3.utils.isAddress(rot13(ref))) {
        cookies.set('ref', ref)
      }
    } catch {}
  }, [ref])

  useEffect(() => {
    // Abort controller for Phase-1 essentials
    if (phase1AbortRef.current) {
      phase1AbortRef.current.abort()
    }
    const ac1 = new AbortController()
    phase1AbortRef.current = ac1
    const aborted = () => ac1.signal.aborted

    const fetchEssentialData = async (fromBg = false) => {
      const cacheKey = 'essential:v1'

      // Try ETH price cache first (hydrate state if available and not background)
      const ethCache = cacheGet('ethPriceMap:v1')
      if (ethCache && !fromBg) {
        setEtherPrice(ethCache)
      }

      // If we have cached essentials and this is not a background refresh,
      // hydrate state immediately for instant paint, schedule bg refresh, and return.
      const cached = cacheGet(cacheKey)
      if (cached && !fromBg) {
        try {
          const { contractData: cachedContracts, ethPriceMap: cachedEth } =
            cached
          if (cachedEth) setEtherPrice(cachedEth)
          if (cachedContracts && cachedEth) {
            // Guard: only update if essentials actually changed
            const sameEssentials = (() => {
              try {
                const prev = essentialData
                if (!prev) return false
                const prevLen = Array.isArray(prev.contractData)
                  ? prev.contractData.length
                  : -1
                const nextLen = Array.isArray(cachedContracts)
                  ? cachedContracts.length
                  : -2
                if (prevLen !== nextLen) return false
                const keyOf = x =>
                  `${x.description}|${x.chainId}|${x.multicallAddress}|${x.versionType}`
                const prevKeys = new Set(prev.contractData.map(keyOf))
                for (const it of cachedContracts)
                  if (!prevKeys.has(keyOf(it))) return false
                const pk = Object.keys(prev.ethPriceMap || {})
                  .sort()
                  .join(',')
                const nk = Object.keys(cachedEth || {})
                  .sort()
                  .join(',')
                return pk === nk
              } catch {
                return false
              }
            })()
            if (!sameEssentials) {
              setEssentialData({
                contractData: cachedContracts,
                ethPriceMap: cachedEth
              })
            }
          }
          setLoadingEssential(false)
        } catch {}
        // Hydrated from cache: schedule a background refresh and bail to prevent duplicate foreground fetch
        setTimeout(() => fetchEssentialData(true), 0)
        return
      }

      // No cache: show spinner and proceed with the foreground fetch
      if (!fromBg) setLoadingEssential(true)

      log(`🟡 [PHASE 1${fromBg ? ' bg' : ''}] Starting essential data fetch...`)
      try {
        log('🔄 Fetching ETH prices...')
        const ethPrices = await Promise.all(
          registryChainIds.map(async chainId => {
            const res = await fetchWithTimeout(
              ethPriceApiUrl[chainId],
              { signal: ac1.signal },
              4000
            )
            const data = await res.json()
            return [chainId, data.USD]
          })
        )
        const ethPriceMap = Object.fromEntries(ethPrices)
        // Guard state updates if aborted
        if (aborted()) return
        setEtherPrice(ethPriceMap)
        // Cache ETH price map for 5 minutes
        cacheSet('ethPriceMap:v1', ethPriceMap, 300)
        if (!fromBg) {
          log('✅ ETH prices loaded')
        } else {
          log('✅ [bg] ETH prices refreshed')
        }

        log('🔄 Fetching contract data (multichain)...')
        // Build a flat list of all chain/config pairs once
        const allConfigs = registryChainIds.flatMap(chainId =>
          getAllMulticallAddresses(chainId).map(cfg => ({ ...cfg, chainId }))
        )
        // Foreground-only: one concise ABI log line
        if (!fromBg) {
          const chains = Array.from(
            new Set(allConfigs.map(c => c.chainId))
          ).join(', ')
          log(
            `[ABI] Loading ABIs for ${allConfigs.length} configs across chains: [${chains}]`
          )
        }

        // Merge new essentials into previous without duplicates
        const mergeEssentials = (prev, additions) => {
          const keyOf = x =>
            `${x.description}|${x.chainId}|${x.multicallAddress}|${x.versionType}`
          const seen = new Set()
          const out = []
          const prevList =
            prev && Array.isArray(prev.contractData) ? prev.contractData : []
          for (const it of prevList) {
            const k = keyOf(it)
            if (!seen.has(k)) {
              seen.add(k)
              out.push(it)
            }
          }
          for (const it of additions) {
            const k = keyOf(it)
            if (!seen.has(k)) {
              seen.add(k)
              out.push(it)
            }
          }
          return out
        }
        // Micro-batched state updates to avoid too many renders while streaming
        const pending = []
        let scheduled = false
        const flushPending = () => {
          if (aborted() || pending.length === 0) {
            scheduled = false
            return
          }
          const adds = pending.splice(0, pending.length)
          startTransition(() => {
            setEssentialData(prev => ({
              contractData: mergeEssentials(prev, adds),
              ethPriceMap
            }))
          })
          // First paint as soon as something is available
          if (!fromBg) setLoadingEssential(false)
          scheduled = false
        }
        const scheduleFlush = () => {
          if (scheduled) return
          scheduled = true
          setTimeout(flushPending, 120)
        }

        // Mark streaming active (we'll turn it off after the final merge)
        phase1StreamingRef.current = true
        phase2HasRunDuringStreamRef.current = false
        // Reset final-done marker at the start of a new Phase-1 streaming session
        setPhase2FinalDone(false)

        // Kick off all requests, but stream successes into state as they resolve
        const perConfigTasks = allConfigs.map(
          async ({ chainId, ...config }) => {
            try {
              const multicallAbi =
                config.type === 'v2'
                  ? getAbi('multicall', 'v2')
                  : getAbi('multicall', 'v4')

              const calls = []
              if (config.type !== 'v2') {
                calls.push(
                  callWithTimeoutAndRetry(
                    `getMainInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getMainInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  ),
                  callWithTimeoutAndRetry(
                    `getOtherInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getOtherInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  ),
                  callWithTimeoutAndRetry(
                    `getRestInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getRestInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  ),
                  callWithTimeoutAndRetry(
                    `getTokenAddresses chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getTokenAddresses',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  )
                )
              } else {
                calls.push(
                  callWithTimeoutAndRetry(
                    `getMainInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getMainInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  ),
                  callWithTimeoutAndRetry(
                    `getOtherInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getOtherInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  ),
                  callWithTimeoutAndRetry(
                    `getRestInfo chain ${chainId}`,
                    () =>
                      readContract(wagmiAdapter.wagmiConfig, {
                        address: config.multicallAddress,
                        abi: multicallAbi,
                        functionName: 'getRestInfo',
                        chainId,
                        signal: ac1.signal
                      }),
                    8000,
                    5000
                  )
                )
              }

              let mainInfo,
                otherInfo,
                restInfo,
                tokenInfo = [],
                accumFeeAmt = null
              if (config.type !== 'v2') {
                ;[mainInfo, otherInfo, restInfo, tokenInfo = []] =
                  await Promise.all(calls)
              } else {
                ;[mainInfo, otherInfo, restInfo] = await Promise.all(calls)
              }

              const entry = {
                chainId,
                ...config,
                description: config.description,
                multicallAddress: config.multicallAddress,
                versionType: config.type || 'v4',
                mainInfo,
                otherInfo,
                restInfo,
                tokenInfo,
                accumFeeAmt
              }

              // Stream this chain into state (batched)
              pending.push(entry)
              scheduleFlush()
              return entry
            } catch (err) {
              // Keep warning in dev, ignore in prod
              warn(
                `❌ Failed fetching for ${config.description} on chain ${chainId}`,
                err
              )
              return null
            }
          }
        )

        // Wait for all to settle for cache/logging consistency
        const allSettled = await Promise.allSettled(perConfigTasks)
        if (aborted()) return
        const contractData = allSettled
          .filter(r => r.status === 'fulfilled' && r.value !== null)
          .map(r => r.value)

        // Ensure any remaining pending entries are flushed
        if (pending.length) {
          flushPending()
        }

        if (!fromBg) {
          log(`✅ Contract data loaded: ${contractData.length} entries`)
        } else {
          log(`✅ [bg] Contract data refreshed: ${contractData.length} entries`)
        }

        // Cache essentials for ~3 minutes
        cacheSet(cacheKey, { contractData, ethPriceMap }, 180)

        // Final merge to ensure state has the complete set (without duplicates)
        startTransition(() => {
          setEssentialData(prev => ({
            contractData: mergeEssentials(prev, contractData),
            ethPriceMap
          }))
        })
        // Signal that streaming is complete; a final Phase-2 may run once
        phase1StreamingRef.current = false
      } catch (e) {
        console.error('❌ [PHASE 1] Failed loading essential data:', e)
        // Guard before setting loadingEssential false
        if (aborted()) return
        setLoadingEssential(false)
      }
    }

    fetchEssentialData(false)
    return () => {
      if (phase1AbortRef.current) {
        phase1AbortRef.current.abort()
      }
    }
  }, [])

  useEffect(() => {
    if (!essentialData) return
    const size = Array.isArray(essentialData?.contractData)
      ? essentialData.contractData.length
      : 0
    if (size === 0) return

    // Coordination rules:
    // 1) While Phase-1 is streaming, run Phase-2 at most once (for early paint).
    // 2) After streaming ends, run Phase-2 only if we haven't processed this final size yet.
    if (phase1StreamingRef.current) {
      if (phase2HasRunDuringStreamRef.current) {
        return // already ran once during this streaming session
      }
      phase2HasRunDuringStreamRef.current = true
    } else {
      // streaming ended; avoid duplicate finals
      if (size <= (lastPhase2ProcessedSizeRef.current || 0)) {
        return
      }
    }

    const fetchDeferredData = async () => {
      // Abort previous Phase 2 work if any
      if (phase2AbortRef.current) {
        phase2AbortRef.current.abort()
      }
      const ac = new AbortController()
      phase2AbortRef.current = ac
      const aborted = () => ac.signal.aborted

      setLoadingDeferred(true)
      // Not final yet while Phase-2 is running
      setPhase2FinalDone(false)
      // Label for Phase-2: (early) if streaming, (final) if not
      const phase2Label = phase1StreamingRef.current ? '(early)' : '(final)'
      log(
        `🟡 [PHASE 2] Starting deferred data fetch ${phase2Label} (history & holders)...`
      )

      // Avoid duplicate fee logs per label/chain in a single run
      const seenFeeLogs = new Set()
      const seenInfoAddr = new Set()

      const { contractData, ethPriceMap } = essentialData

      // 🔎 Compute per-contract accumulated fees SAFELY by summing per-pool via HotCurvesInfo.getAccumFee
      const updatedContractData = await Promise.all(
        contractData.map(async entry => {
          // Skip legacy V1 entirely – they don't have fees
          const isLegacyV1 =
            /v1/i.test(entry.description || '') || entry.versionType === 'v1'
          if (isLegacyV1) {
            return { ...entry, accumFeeAmt: undefined, __skipFees: true }
          }
          if (entry.versionType === 'v2') return entry

          const logPfx = `[Fees][${entry.description} | chain ${entry.chainId}]`
          const pools = Array.isArray(entry.mainInfo?.[5])
            ? entry.mainInfo[5]
            : []
          const lpArray = Array.isArray(entry?.restInfo?.[1])
            ? entry.restInfo[1]
            : []
          const lpTrueIdx = lpArray
            .map((v, idx) => (v === true || v === 'true' || v === 1 ? idx : -1))
            .filter(idx => idx !== -1)
          const lpCount = lpTrueIdx.length
          const hasAnyLp = lpCount > 0
          // Collapse logging: only warn if length mismatch, concise skips
          const poolsToQuery = lpTrueIdx.map(i => pools[i]).filter(Boolean)
          if (
            pools.length &&
            lpArray.length &&
            pools.length !== lpArray.length
          ) {
            warn(
              `${logPfx} pools vs lp flags length mismatch: ${pools.length} vs ${lpArray.length}`
            )
          }
          if (!hasAnyLp) {
            return { ...entry, accumFeeAmt: null }
          }
          if (poolsToQuery.length === 0) {
            return { ...entry, accumFeeAmt: null }
          }
          try {
            const toBigIntFee = val => {
              try {
                // direct bigint
                if (typeof val === 'bigint') return val
                // wagmi readContracts item { status, result }
                if (val && typeof val === 'object' && 'result' in val) {
                  const res = val.result
                  if (typeof res === 'bigint') return res
                  if (Array.isArray(res)) {
                    // tuple return; first item is usually the uint256
                    const first = res[0]
                    if (typeof first === 'bigint') return first
                    if (first != null) return BigInt(first)
                    return 0n
                  }
                  // viem sometimes returns Hex or object with .toString
                  if (res && typeof res === 'object' && 'toString' in res) {
                    return BigInt(res.toString())
                  }
                  if (res != null) return BigInt(res)
                }
                // viem direct value (hex string / decimal string / array-like)
                if (Array.isArray(val)) {
                  const first = val[0]
                  if (typeof first === 'bigint') return first
                  if (first != null) return BigInt(first)
                }
                if (val && typeof val === 'object' && '0' in val) {
                  // tuple-like object {0: ..., 1: ...}
                  const first = val[0]
                  if (typeof first === 'bigint') return first
                  if (first != null) return BigInt(first)
                }
                if (val != null) return BigInt(val)
              } catch {}
              return 0n
            }
            const infoAbi = Array.isArray(InfoAbiV2?.abi)
              ? InfoAbiV2.abi
              : Array.isArray(InfoAbiV2)
              ? InfoAbiV2
              : []
            if (!Array.isArray(infoAbi) || infoAbi.length === 0) {
              warn(`${logPfx} Info ABI is empty/invalid; cannot read fees`)
              return { ...entry, accumFeeAmt: null }
            }
            const infoAddress = entry.infoAddress
            if (!infoAddress) {
              warn(`${logPfx} no HotCurvesInfo address; cannot read fees`)
              return { ...entry, accumFeeAmt: null }
            }
            const infoKey = `${entry.description}:${entry.chainId}:${infoAddress}`
            if (!seenInfoAddr.has(infoKey)) {
              seenInfoAddr.add(infoKey)
            }
            // Build one multicall per chain for all pools' getAccumFee(poolAddr)
            const contracts = poolsToQuery.map((poolAddr, localIdx) => ({
              address: infoAddress,
              abi: infoAbi,
              functionName: 'getAccumFee',
              args: [poolAddr],
              chainId: entry.chainId
            }))
            if (contracts.length === 0) {
              log(`${logPfx} no pools to query after filtering; returning null`)
              return { ...entry, accumFeeAmt: null }
            }
            let results
            try {
              results = await callWithTimeoutAndRetry(
                `fees multicall chain ${entry.chainId} (${contracts.length} pools)`,
                () =>
                  readContracts(wagmiAdapter.wagmiConfig, {
                    contracts,
                    allowFailure: true
                  }),
                8000,
                5000
              )
            } catch (e) {
              warn(`${logPfx} multicall failed at RPC level:`, e?.message || e)
              return { ...entry, accumFeeAmt: null }
            }
            // Sum successes; treat failures & div/0 as 0
            let okCount = 0
            let failCount = 0
            const total = results.reduce((sum, r) => {
              try {
                okCount++
                return sum + toBigIntFee(r)
              } catch {
                failCount++
                return sum
              }
            }, 0n)
            // Build a per‑pool fee map aligned to poolsToQuery, normalize address to lower-case
            const perPoolFees = new Map()
            results.forEach((r, idx) => {
              const v = toBigIntFee(r)
              const pAddr = poolsToQuery[idx]
              if (pAddr) perPoolFees.set(String(pAddr).toLowerCase(), v)
            })
            // Collapse per-pool result logs into a single summary and avoid duplicate repeats.
            const feeLogKey = `${entry.description}:${entry.chainId}:${contracts.length}`
            if (!seenFeeLogs.has(feeLogKey)) {
              const nativeTotal = formatUnitsNum(total, 18)
              const line =
                `${logPfx} fees: ok=${okCount}/${contracts.length}` +
                (failCount ? ` fail=${failCount}` : '') +
                ` totalWei=${total.toString()} native≈${nativeTotal}`
              if (failCount) {
                warn(line)
              } else {
                log(line)
              }
              seenFeeLogs.add(feeLogKey)
            }
            if (okCount === 0) {
              log(
                `${logPfx} all results failed (likely uninitialized price routes)`
              )
              return { ...entry, accumFeeAmt: null }
            }
            return { ...entry, accumFeeAmt: total, __perPoolFees: perPoolFees }
          } catch (e) {
            warn(`${logPfx} per-pool getAccumFee failed:`, e?.message)
            return { ...entry, accumFeeAmt: null }
          }
        })
      )

      let contractDetails = []
      let totalAccumFee = 0
      let successChains = 0
      let failedChains = 0
      let skippedChains = 0
      let buildCounter = 0
      for (const {
        chainId,
        mainInfo,
        otherInfo,
        restInfo,
        tokenInfo,
        accumFeeAmt,
        __skipFees,
        __perPoolFees,
        versionType: entryVersion,
        type: entryType,
        canCreate,
        infoAddress,
        id: deploymentId
      } of updatedContractData) {
        const versionType = (entryVersion || entryType || 'v4')?.toLowerCase()
        const isV2Version = versionType === 'v2'
        if (isV2Version) {
          skippedChains += 1
        } else if (__skipFees) {
          skippedChains += 1
        } else if (accumFeeAmt === null) {
          failedChains += 1
        } else {
          successChains += 1
          const localAccumFeeEth = formatUnitsNum(accumFeeAmt ?? 0n, 18)
          totalAccumFee += localAccumFeeEth * ethPriceMap[chainId]
        }
        for (let i = mainInfo[0].length - 1; i >= 0; i--) {
          const contractAddress = mainInfo[5][i]
          const tokenAddress = tokenInfo[i] || mainInfo[7]?.[i] || ''
          const devAddress = mainInfo[6][i]
          // Placeholder for lazy history; will patch visible set later
          const isV2 = isV2Version
          const depositedAmount =
            (Number(mainInfo[4][i]) * ethPriceMap[chainId]) / 1e18
          let marketCap = 0
          let progress = 0
          let liquidity = 0
          const tokenId = restInfo?.[5]?.[i] || null
          // Compute generatedFee for this pool
          const perFeeWei = __perPoolFees
            ? __perPoolFees.get(String(contractAddress).toLowerCase())
            : 0n
          // (Optional debug for DEV) Warn if fee is 0 but map has an entry
          if (DEV && __perPoolFees && __perPoolFees.size && perFeeWei === 0n) {
            const hasAlt = __perPoolFees.has(
              String(contractAddress).toLowerCase()
            )
            if (!hasAlt)
              warn(
                '[fees] no per‑pool fee found for',
                contractAddress,
                'on',
                chainId
              )
          }
          const generatedFee = perFeeWei
            ? formatUnitsNum(perFeeWei, 18) * (ethPriceMap?.[chainId] || 0)
            : 0
          if (isV2) {
            const virtualLpAmounts =
              Number(mainInfo[2][i]) * ethPriceMap[chainId]
            const virtualLpTokenAmounts = Number(mainInfo[1][i]) / 1e18
            const marketCap = virtualLpAmounts / virtualLpTokenAmounts / 1e9
            const lpFlag = Boolean(restInfo?.[1]?.[i])
            const tokenPriceRaw = Number(mainInfo[3][i])
            progress = calculateBondingProgress({
              chainId,
              versionType,
              tokenPriceRaw,
              lpCreated: lpFlag
            })
            liquidity = virtualLpAmounts * 2
            // Placeholders for history (will patch visible set below)
            const lastAction = 'bought'
            const lastActionNumber = 0
            const restDescriptions =
              Array.isArray(restInfo?.[2]) ? restInfo[2] : []
            const restFeatureTimes =
              Array.isArray(restInfo?.[0]) ? restInfo[0] : []
            const restLpFlags = Array.isArray(restInfo?.[1]) ? restInfo[1] : []
            const restRisingFlags = Array.isArray(restInfo?.[4])
              ? restInfo[4]
              : []
            contractDetails.push({
              contractAddress,
              tokenAddress: '',
              devAddress,
              startTime: Number(mainInfo[0][i]),
              tokenName: otherInfo[0]?.[i] || '',
              tokenSymbol: otherInfo[1]?.[i] || '',
              depositedAmount,
              liquidity,
              marketCap,
              progress,
              tokenDescription: restDescriptions?.[i] || '',
              lpCreated: lpFlag,
              featureTime: Number(restFeatureTimes?.[i] || 0),
              website: otherInfo[2]?.[i] || '',
              twitter: otherInfo[3]?.[i] || '',
              telegram: otherInfo[4]?.[i] || '',
              chainId,
              logoUrl: `${imageUrl}${contractAddress}-logo.png`,
              bannerUrl: `${imageUrl}${contractAddress}-banner.png`,
              blockchainLogoUrl: chainLogos[chainId],
              isRising:
                restRisingFlags?.[i] === true ||
                restRisingFlags?.[i] === 'true',
              lastAction,
              lastActionNumber,
              tokenId,
              generatedFee,
              accumFeeUsd: generatedFee,
              accumFeeUSD: generatedFee,
              versionType,
              canCreate: Boolean(canCreate),
              infoAddress: infoAddress || null,
              deploymentId: deploymentId || null
            })
            buildCounter++
            if (buildCounter % 25 === 0) {
              // Yield to the event loop so input stays snappy
              // eslint-disable-next-line no-await-in-loop
              await microYield()
            }
          } else {
            const tokenAmount = Number(mainInfo[1][i]) / 1e18 // token supply
            const usdAmount = Number(mainInfo[2][i]) * ethPriceMap[chainId] // ETH value
            marketCap = usdAmount / tokenAmount / 1e9
            const lpFlag = Boolean(restInfo?.[1]?.[i])
            const tokenPriceRaw = Number(mainInfo[3][i])
            progress = calculateBondingProgress({
              chainId,
              versionType,
              tokenPriceRaw,
              lpCreated: lpFlag
            })
            liquidity = Number(mainInfo[2]?.[i] || 0) * ethPriceMap[chainId] * 2
            // Placeholders for history (will patch visible set below)
            const lastAction = 'bought'
            const lastActionNumber = 0
            contractDetails.push({
              contractAddress,
              tokenAddress,
              devAddress,
              startTime: Number(mainInfo[0][i]),
              tokenName: otherInfo[0]?.[i] || '',
              tokenSymbol: otherInfo[1]?.[i] || '',
              depositedAmount,
              liquidity,
              marketCap,
              progress,
              tokenDescription: restInfo?.[2]?.[i] || '',
              lpCreated: lpFlag,
              featureTime: Number(restInfo?.[0]?.[i] || 0),
              website: otherInfo[2]?.[i] || '',
              twitter: otherInfo[3]?.[i] || '',
              telegram: otherInfo[4]?.[i] || '',
              chainId,
              lastAction,
              lastActionNumber,
              logoUrl: `${imageUrl}${contractAddress}-${chainId}-logo.png`,
              bannerUrl: `${imageUrl}${contractAddress}-banner.png`,
              blockchainLogoUrl: chainLogos[chainId],
              isRising:
                restInfo?.[4]?.[i] === true || restInfo?.[4]?.[i] === 'true',
              tokenId,
              generatedFee,
              versionType,
              canCreate: Boolean(canCreate),
              infoAddress: infoAddress || null,
              deploymentId: deploymentId || null
            })
            buildCounter++
            if (buildCounter % 25 === 0) {
              // Yield to the event loop so input stays snappy
              // eslint-disable-next-line no-await-in-loop
              await microYield()
            }
          }
        }
        if (!aborted()) {
          setAccumFee(totalAccumFee)
        }
      }

      const ZERO_ADDRESS = '0x0000000000000000000000000000000000000000'
      if (contractDetails.length) {
        const needsCreatorLookup = contractDetails.filter(entry => {
          const dev = String(entry.devAddress || '').toLowerCase()
          return !dev || dev === ZERO_ADDRESS
        })

        if (needsCreatorLookup.length) {
          try {
            const creatorResults = await Promise.allSettled(
              needsCreatorLookup.map(entry =>
                fetchDevAddress({
                  wagmiConfig: wagmiAdapter.wagmiConfig,
                  contractAddress: entry.contractAddress,
                  chainId: entry.chainId,
                  versionType: entry.versionType,
                  signal: ac1.signal
                })
              )
            )
            const creatorMap = new Map()
            creatorResults.forEach((res, idx) => {
              if (res.status !== 'fulfilled') return
              const value = String(res.value || '').toLowerCase()
              if (!value || value === ZERO_ADDRESS) return
              const key = needsCreatorLookup[idx].contractAddress.toLowerCase()
              creatorMap.set(key, String(res.value))
            })
            if (creatorMap.size) {
              contractDetails = contractDetails.map(entry => {
                const fallbackDev = creatorMap.get(
                  String(entry.contractAddress).toLowerCase()
                )
                if (!fallbackDev) return entry
                return { ...entry, devAddress: fallbackDev }
              })
            }
          } catch (err) {
            warn('creator lookup failed', err)
          }
        }
      }

      // First paint as soon as contract details are ready (don't wait for history/holders/fees)
      if (Array.isArray(contractDetails) && contractDetails.length > 0) {
        setChadListData(prev =>
          prev && prev.length > 0 ? prev : [...contractDetails]
        )
        // Latch: stop all skeletons/blink after the very first real list
        setFirstPaintDone(true)
      }

      // —— Lazy-load HISTORY only for visible top slice ——
      const topSlice = [...contractDetails]
        .sort((a, b) => b.progress - a.progress)
        .slice(0, 12)
      const visibleByAddr = new Set(topSlice.map(x => x.contractAddress))
      const limiter = mkLimiter(6)
      const histories = await Promise.allSettled(
        topSlice.map(pool =>
          limiter(async () => {
            const key = `hist:${pool.contractAddress}`
            const cached = cacheGet(key)
            if (cached) return { addr: pool.contractAddress, data: cached }
            const r = await fetchWithTimeout(
              `${apiUrl}/api/getHistoryByContract/${pool.contractAddress}`,
              { signal: ac.signal },
              6000
            )
            const j = await r.json()
            cacheSet(key, j, 120)
            return { addr: pool.contractAddress, data: j }
          })
        )
      )
      const historyMap = new Map()
      histories.forEach(h => {
        if (h.status === 'fulfilled') historyMap.set(h.value.addr, h.value.data)
      })

      // Guard state updates if aborted
      if (aborted()) return
      // Patch lastAction fields for only the visible items
      contractDetails = contractDetails.map(item => {
        if (!visibleByAddr.has(item.contractAddress)) return item
        const hist = historyMap.get(item.contractAddress) || []
        if (!Array.isArray(hist) || hist.length === 0) return item
        const lastAction = hist[0]?.type || 'bought'
        let lastActionNumber = 0
        for (const ev of hist) {
          if (ev?.type === lastAction) lastActionNumber++
          else break
        }
        return { ...item, lastAction, lastActionNumber }
      })

      // First paint: list without holders
      let fullList = [...contractDetails]
      // Defer holders fetching for the visible slice
      const visibleTokens = topSlice
        .filter(x => x.tokenAddress)
        .map(x => ({
          tokenAddress: x.tokenAddress,
          chainId: x.chainId,
          devAddress: x.devAddress
        }))
      const scheduleIdle = fn => {
        if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
          window.requestIdleCallback(() => {
            if (!aborted()) fn()
          })
        } else {
          setTimeout(() => {
            if (!aborted()) fn()
          }, 200)
        }
      }
      scheduleIdle(async () => {
        const holdLimiter = mkLimiter(4)
        const holdersData = await Promise.allSettled(
          visibleTokens.map(({ tokenAddress, chainId, devAddress }) =>
            holdLimiter(async () => {
              const cacheKey = `holders:${chainId}:${tokenAddress}`
              const cached = cacheGet(cacheKey)
              if (cached) return { tokenAddress, data: cached }
              const url =
                chainId !== 964
                  ? `https://deep-index.moralis.io/api/v2.2/erc20/${tokenAddress}/owners?chain=0x${Number(
                      chainId
                    ).toString(16)}&order=DESC`
                  : `https://bittensorexplorer.hotkeyswap.com/api/v2/tokens/${tokenAddress}/holders`
              try {
                const res = await fetchWithTimeout(
                  url,
                  {
                    method: 'GET',
                    headers:
                      chainId !== 964
                        ? {
                            accept: 'application/json',
                            'X-API-Key':
                              'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJub25jZSI6IjIxNDczNDI5LTAyZDktNDZkYi1hYzA4LTBjMmUwM2Y0MGNmZSIsIm9yZ0lkIjoiMzc5NzM5IiwidXNlcklkIjoiMzkwMjA2IiwidHlwZSI6IlBST0pFQ1QiLCJ0eXBlSWQiOiIzMTNmMTM4Mi02ZmJmLTQxYzAtYjYwNy0zOTAwY2NiYmQyNTUiLCJpYXQiOjE3NDM1Mjk1ODcsImV4cCI6NDg5OTI4OTU4N30.1-bRbsgC6wshiiw_LwdfjoUY4eAfAjJo5B9h8Te1vN8'
                          }
                        : {},
                    signal: ac.signal
                  },
                  7000
                )
                if (!res.ok) {
                  warn(
                    `holders fetch non-200 for ${tokenAddress} on ${chainId}: ${res.status}`
                  )
                }
                const data = await res.json()
                const holders = data.result || data.items || []
                const top15 = holders
                  .slice(0, 15)
                  .reduce(
                    (sum, h) => sum + h.percentage_relative_to_total_supply,
                    0
                  )
                const dev =
                  holders.find(
                    h =>
                      h.owner_address?.toLowerCase?.() ===
                      devAddress.toLowerCase()
                  )?.percentage_relative_to_total_supply || 0
                const packed = {
                  tokenAddress,
                  top15HoldersPercent: top15,
                  devPercent: dev,
                  holdersNumber: holders.length
                }
                cacheSet(cacheKey, packed, 180)
                return { tokenAddress, data: packed }
              } catch {
                return {
                  tokenAddress,
                  data: {
                    tokenAddress,
                    top15HoldersPercent: 0,
                    devPercent: 0,
                    holdersNumber: 0
                  }
                }
              }
            })
          )
        )
        if (aborted()) return
        const hMap = new Map()
        holdersData.forEach(r => {
          if (r.status === 'fulfilled')
            hMap.set(r.value.tokenAddress, r.value.data)
        })
        setChadListData(prev =>
          prev.map(it => ({ ...it, ...(hMap.get(it.tokenAddress) || {}) }))
        )
      })

      const showDash = successChains === 0
      const summaryLine = `[Fees][summary] USD total=${Number(
        totalAccumFee
      ).toFixed(
        2
      )} successChains=${successChains} failedChains=${failedChains} skippedChains=${skippedChains} showDash=${showDash}`
      if (lastFeesSummaryRef.current !== summaryLine) {
        log(summaryLine)
        lastFeesSummaryRef.current = summaryLine
      }
      // Guard state updates if aborted
      if (aborted()) return
      setAccumFeeUnavailable(showDash)
      setChadListData(prev =>
        Array.isArray(fullList) && fullList.length > 0 ? fullList : prev
      )
      setAccumFee(totalAccumFee)
      setLoadingDeferred(false)
      // If this pass was the final (non-streaming) pass, mark final done
      if (!phase1StreamingRef.current) {
        setPhase2FinalDone(true)
      }

      log(`🟢 [PHASE 2] Deferred data loaded and merged. ${phase2Label}`)
      log(
        `🧪 Final contract list with holders: count=${fullList.length} ${phase2Label}`
      )
      // Remember the last essentials size fully processed by Phase-2
      lastPhase2ProcessedSizeRef.current = Array.isArray(
        essentialData?.contractData
      )
        ? essentialData.contractData.length
        : fullList.length
    }

    fetchDeferredData()
    return () => {
      if (phase2AbortRef.current) {
        phase2AbortRef.current.abort()
      }
    }
  }, [essentialData])

  useEffect(() => {
    const storedWatchlist = JSON.parse(localStorage.getItem('watchlist')) || []
    setWatchlist(storedWatchlist)
  }, [])

  useEffect(() => {
    localStorage.setItem('watchlist', JSON.stringify(watchlist))
  }, [watchlist])

  const toggleWatchlist = useCallback((addressOrKey, chainId) => {
    if (!addressOrKey) return
    // If a composite key like "addr:chainId" is passed directly and no chainId arg,
    // toggle that key as-is (lowercased).
    if (
      typeof addressOrKey === 'string' &&
      addressOrKey.includes(':') &&
      chainId === undefined
    ) {
      const key = addressOrKey.toLowerCase()
      setWatchlist(prev =>
        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
      )
      return
    }
    // Otherwise build preferred key from address + optional chainId
    const addr = String(addressOrKey).toLowerCase()
    const key = wlKey(addr, chainId)
    setWatchlist(prev => {
      const list = Array.isArray(prev) ? prev : []
      const hasNew = list.includes(key)
      const hasOld = list.includes(addr) // legacy plain address entry
      if (hasNew || hasOld) {
        // Toggle OFF: remove both forms so shared-address coins on other chains aren't affected
        return list.filter(k => k !== key && k !== addr)
      }
      // Toggle ON: add the composite if we have chainId, else the plain addr
      return [...list, key]
    })
  }, [])

  const sortOptions = [
    { value: 'Market Cap', label: 'sort: market cap' },
    { value: 'Time', label: 'sort: time' },
    { value: 'Volume', label: 'sort: volume' },
    { value: 'Last Reply', label: 'sort: last reply' }
  ]

  const orderOptions = [
    { value: 'Descending', label: 'Descending' },
    { value: 'Ascending', label: 'Ascending' }
  ]

  const statusOptions = [
    { value: 'All', label: 'status: all' },
    { value: 'Listed', label: 'status: Bonded' },
    { value: 'Live', label: 'status: live' },
    { value: 'Favorite', label: 'status: favorite' }
  ]

  const chainOptions = [
    { value: 'Chains', label: 'chain: all' },
    { value: 'Ethereum', label: 'chain: ethereum' },
    { value: 'BSC', label: 'chain: bsc' },
    { value: 'Base', label: 'chain: base' },
    { value: 'Arbitrum', label: 'chain: arbitrum' },
    { value: 'SubtensorEVM', label: 'chain: bittensor' }
  ]

  function FilterSelect({ options, defaultValue, onChange }) {
    const handleChange = newValue => {
      onChange(newValue)
    }

    return (
      <Select
        defaultValue={defaultValue}
        isSearchable={false}
        options={options}
        value={options.find(option => option.value === defaultValue.value)}
        onChange={handleChange}
        menuPortalTarget={document.body}
        styles={{
          menuPortal: base => ({ ...base, zIndex: 9999 }),
          control: (styles, state) => ({
            ...styles,
            backgroundColor: 'rgba(42,45,54,0.40)', // bg-[#2a2d36]/40
            border: `1px solid ${state.isFocused ? '#a135bb' : '#2a2d36'}`,
            borderRadius: '0.5rem', // rounded-lg
            color: '#f8ffe8',
            boxShadow: 'none',
            transition:
              'transform 150ms, background-color 150ms, border-color 150ms',
            ':hover': {
              backgroundColor: '#2a2d36',
              borderColor: '#a135bb',
              transform: 'translateY(-1px)'
            },
            cursor: 'pointer',
            fontSize: '12px'
          }),
          valueContainer: s => ({ ...s, padding: '0 10px' }),
          placeholder: s => ({ ...s, color: 'rgba(248,255,232,0.6)' }),
          singleValue: s => ({ ...s, color: 'rgba(248,255,232,0.9)' }),
          dropdownIndicator: s => ({ ...s, color: 'rgba(248,255,232,0.9)' }),
          indicatorSeparator: () => ({ display: 'none' }),
          menu: s => ({
            ...s,
            backgroundColor: 'rgba(42,45,54,0.95)',
            border: '1px solid #2a2d36',
            borderRadius: '0.5rem',
            marginTop: '0.25rem',
            zIndex: 50
          }),
          menuList: s => ({ ...s, padding: 4 }),
          option: (s, { isFocused, isSelected }) => ({
            ...s,
            backgroundColor: isSelected
              ? '#2a2d36'
              : isFocused
              ? '#22262e'
              : 'rgba(42,45,54,0.95)',
            color: '#f8ffe8',
            cursor: 'pointer',
            fontSize: '12px'
          })
        }}
      />
    )
  }

  const [sortValue, setSortValue] = useState(sortOptions[0])
  const [orderValue, setOrderValue] = useState(orderOptions[0])
  const [statusValue, setStatusValue] = useState(statusOptions[0])
  const [chainValue, setChainValue] = useState(chainOptions[0])

  // ---- Graduated (Bonded) list & latch (top-level hooks; not inside useMemo) ----
  const graduatedItems = useMemo(
    () => chadLists.filter(item => item.progress >= 100),
    [chadLists]
  )

  const graduatedByFees = useMemo(() => {
    const getFee = p => {
      const v =
        p.accumFeeUsd ??
        p.accumFeeUSD ??
        p.generatedFee ??
        p.feeUsd ??
        p.feesUsd ??
        p.feeUSD ??
        0
      const n = Number(v)
      return Number.isFinite(n) ? n : 0
    }
    const arr = Array.isArray(graduatedItems) ? [...graduatedItems] : []
    arr.sort((a, b) => getFee(b) - getFee(a))
    return arr
  }, [graduatedItems])

  useEffect(() => {
    if (graduatedPaintDone) return
    if (graduatedItems.length > 0) {
      setGraduatedPaintDone(true)
    } else if (phase2FinalDone && !phase1StreamingRef.current) {
      // Final dataset is in and there are no graduated items; stop the skeleton
      setGraduatedPaintDone(true)
    }
  }, [graduatedItems.length, phase2FinalDone, graduatedPaintDone])

  const timeFilterChadLists = useMemo(() => {
    let timeFilteredList = []
    const now = Date.now() / 1000
    switch (selectedItem) {
      case '15m':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 900 >= now)
        ]
        break
      case '1H':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 3600 >= now)
        ]
        break
      case '6H':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 21600 >= now)
        ]
        break
      case '24H':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 86400 >= now)
        ]
        break
      case '1W':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 604800 >= now)
        ]
        break
      case '2W':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 1209600 >= now)
        ]
        break
      case '1M':
        timeFilteredList = [
          ...chadLists.filter(item => Number(item.startTime) + 2592000 >= now)
        ]
        break
      case 'All time':
        timeFilteredList = [...chadLists]
        break
      default:
        break
    }
    return timeFilteredList
  }, [selectedItem, chadLists])

  // Pre-calc derived totals
  const { totalVol, totalMc } = useMemo(() => {
    const vol = (timeFilterChadLists || []).reduce(
      (sum, p) => sum + (p.depositedAmount || 0),
      0
    )
    const mc = (timeFilterChadLists || []).reduce(
      (sum, p) => sum + (p.marketCap || 0),
      0
    )
    return {
      totalVol: Math.round(vol * 100) / 100,
      totalMc: Math.round(mc * 100) / 100
    }
  }, [timeFilterChadLists])

  // Given a sorted list, return a version with “hot” items first (stable partition)
  const reorderByRecentBumps = useCallback(
    sorted => {
      if (!liveBuzzEnabled) return sorted
      const lb = lastBumpRef.current
      if (!lb || lb.size === 0) return sorted

      // decorate with original index for stable tie-breaking
      const decorated = sorted.map((item, idx) => ({
        item,
        idx,
        ts:
          lb.get(
            String(item.contractAddress || item.address || '').toLowerCase()
          ) || 0
      }))

      // most recent first; preserve original order for ties
      decorated.sort((a, b) => b.ts - a.ts || a.idx - b.idx)

      return decorated.map(d => d.item)
    },
    [liveBuzzEnabled]
  )

  // Helper used by LaunchpadCard in step 2
  const isAddrHot = useCallback(addr => {
    const exp = recentBuysRef.current.get(String(addr || '').toLowerCase())
    return !!exp && exp > Date.now()
  }, [])

  const onSearchChanged = event => {
    setSearchInput(event.target.value)
  }

  const onSortChange = newValue => {
    startTransition(() => setSortValue(newValue))
  }

  const onChainChange = val => {
    startTransition(() => setChainValue(val))
  }

  const onOrderChange = () => {
    startTransition(() => {
      if (orderValue.value === 'Descending') setOrderValue(orderOptions[1])
      else setOrderValue(orderOptions[0])
    })
  }

  const onStatusChange = newValue => {
    startTransition(() => setStatusValue(newValue))
  }
  const onResetFilters = () => {
    try {
      setSortValue(sortOptions[0])
      setOrderValue(orderOptions[0])
      setStatusValue(statusOptions[0])
      setChainValue(chainOptions[0])
      setSearchInput('')
      startTransition(() => setSearch(''))
      setBuyAmount('1')
    } catch {}
  }
  // Use deferred value for smoother filtering during typing
  const deferredSearch = useDeferredValue(search)
  const searchNeedle = useMemo(
    () => (deferredSearch || '').toLowerCase().trim(),
    [deferredSearch]
  )

  // Client-side pagination for the main grid
  const PAGE_SIZE = 18
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE)

  // Only auto-load after the user scrolls a bit (prevents instant full expansion)
  const [userScrolled, setUserScrolled] = useState(false)
  const hasSetScrolledRef = useRef(false)
  useEffect(() => {
    // If the user is already scrolled on load, set once and bail
    if (
      !hasSetScrolledRef.current &&
      typeof window !== 'undefined' &&
      window.scrollY > 50
    ) {
      hasSetScrolledRef.current = true
      setUserScrolled(true)
      return
    }
    const onScroll = () => {
      if (!hasSetScrolledRef.current && window.scrollY > 50) {
        hasSetScrolledRef.current = true
        setUserScrolled(true)
        window.removeEventListener('scroll', onScroll)
      }
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Reset page when filters/sort/search/time window change
  useEffect(() => {
    startTransition(() => setVisibleCount(PAGE_SIZE))
  }, [
    sortValue,
    orderValue,
    statusValue,
    chainValue,
    deferredSearch,
    selectedItem
  ])

  const loadMoreRef = useRef(null)
  const loadingMoreRef = useRef(false)
  const lastLoadAtRef = useRef(0)

  // Track last Phase-2 summary so we only log when it changes
  const lastFeesSummaryRef = useRef('')

  // SWR latest chats (for "Last Reply" sort)
  const latestChats = useLatestChatsSWR()
  const timestampMap = useMemo(
    () =>
      new Map(
        Array.isArray(latestChats)
          ? latestChats.map(chat => [chat.ChadAddress, chat.timestamp])
          : []
      ),
    [latestChats]
  )

  const GRAD_PAGE_SIZE = 3
  const [gradVisibleCount, setGradVisibleCount] = useState(GRAD_PAGE_SIZE)
  useEffect(() => {
    setGradVisibleCount(GRAD_PAGE_SIZE)
  }, [graduatedItems.length])

  // Derived: filter -> then sort
  const filteredChadLists = useMemo(() => {
    // Advanced multi-field, multi-term search
    const searchFiltered = chadLists.filter(list => {
      if (!searchNeedle) return true
      const terms = searchNeedle.split(/\s+/).filter(Boolean)
      if (terms.length === 0) return true

      const str = v => String(v == null ? '' : v).toLowerCase()

      // Build a combined haystack across key fields
      const haystackParts = [
        str(list.tokenName),
        str(list.tokenSymbol),
        str(list.tokenDescription || list.description),
        str(list.contractAddress),
        str(list.tokenAddress),
        str(list.devAddress),
        // Optional: include socials so users can search by handle/URL
        str(list.website),
        str(list.twitter),
        str(list.telegram)
      ]
      const haystack = haystackParts.join(' ')

      // AND semantics: all terms must be found somewhere in the haystack
      return terms.every(t => haystack.includes(t))
    })
    let chainFilteredList = []
    switch (chainValue.value) {
      case 'Chains':
        chainFilteredList = [...searchFiltered]
        break
      case 'SubtensorEVM':
        chainFilteredList = searchFiltered.filter(item => item.chainId === 964)
        break
      case 'Base':
        chainFilteredList = searchFiltered.filter(item => item.chainId === 8453)
        break
      case 'Ethereum':
        chainFilteredList = searchFiltered.filter(item => item.chainId === 1)
        break
      case 'Arbitrum':
        chainFilteredList = searchFiltered.filter(
          item => item.chainId === 42161
        )
        break
      case 'BSC':
        chainFilteredList = searchFiltered.filter(item => item.chainId === 56)
        break
      default:
        chainFilteredList = searchFiltered
        break
    }
    switch (statusValue.value) {
      case 'All':
        return chainFilteredList
      case 'Listed':
        return chainFilteredList.filter(
          item => item.lpCreated === true || item.progress >= 100
        )
      case 'Live':
        return chainFilteredList.filter(item => item.progress < 100)
      case 'Favorite':
        return chainFilteredList.filter(item =>
          isWatched(watchlist, item.contractAddress, item.chainId)
        )
      default:
        return chainFilteredList
    }
  }, [chadLists, chainValue, statusValue, searchNeedle, watchlist])

  const sortedChadLists = useMemo(() => {
    let base = [...filteredChadLists]
    switch (sortValue.value) {
      case 'Market Cap': {
        base.sort((a, b) => {
          const av = Number(a.marketCap) || 0
          const bv = Number(b.marketCap) || 0
          return orderValue.value === 'Ascending' ? av - bv : bv - av
        })
        break
      }
      case 'Time': {
        base.sort((a, b) => {
          const av = Number(a.startTime) || 0
          const bv = Number(b.startTime) || 0
          return orderValue.value === 'Ascending' ? av - bv : bv - av
        })
        break
      }
      case 'Volume': {
        base.sort((a, b) => {
          const av = Number(a.depositedAmount) || 0
          const bv = Number(b.depositedAmount) || 0
          return orderValue.value === 'Ascending' ? av - bv : bv - av
        })
        break
      }
      case 'Last Reply': {
        base.sort((a, b) => {
          const av = Number(timestampMap.get(a.contractAddress)) || 0
          const bv = Number(timestampMap.get(b.contractAddress)) || 0
          return orderValue.value === 'Ascending' ? av - bv : bv - av
        })
        break
      }
      default: {
        // no-op; keep incoming order after filters
        break
      }
    }
    base.sort((a, b) => {
      const isA = isWatched(watchlist, a.contractAddress, a.chainId)
      const isB = isWatched(watchlist, b.contractAddress, b.chainId)
      return (isB ? 1 : 0) - (isA ? 1 : 0)
    })
    return base
  }, [filteredChadLists, sortValue, orderValue, watchlist])

  // Infinite scroll: sentinel & guard (must be after sortedChadLists is defined)
  useEffect(() => {
    const el = loadMoreRef.current
    if (!el) return

    const observer = new IntersectionObserver(
      entries => {
        const entry = entries[0]
        if (!entry || !entry.isIntersecting) return

        // Early guards to prevent duplicate increments or churn during state flips
        if (loadingMoreRef.current) return
        if (loadingEssential || loadingDeferred) return
        if (!userScrolled) return
        if (visibleCount >= sortedChadLists.length) return

        // Cooldown: avoid rapid-fire triggers from intersecting while layout settles
        const now = Date.now()
        if (now - (lastLoadAtRef.current || 0) < 300) return
        lastLoadAtRef.current = now

        loadingMoreRef.current = true
        startTransition(() =>
          setVisibleCount(c => Math.min(c + PAGE_SIZE, sortedChadLists.length))
        )

        // Release the lock shortly after paint; small buffer for layout reflow
        setTimeout(() => {
          loadingMoreRef.current = false
        }, 300)
      },
      { root: null, rootMargin: '100px', threshold: 0 }
    )

    observer.observe(el)
    // If everything is already visible, disconnect early
    if (visibleCount >= sortedChadLists.length) {
      observer.unobserve(el)
    }
    return () => observer.disconnect()
  }, [
    userScrolled,
    visibleCount,
    sortedChadLists.length,
    loadingEssential,
    loadingDeferred
  ])
  // const [isHovering, setIsHovering] = useState(false);
  // const handleMouseEnter = () => {
  //   setIsHovering(true);
  //   setIsHovering(false);
  // };

  // const handleMouseLeave = () => {
  //   setIsHovering(false);
  // };

  let refAddress
  if (cookies.get('ref')) {
    if (Web3.utils.isAddress(rot13(cookies.get('ref')))) {
      refAddress = rot13(cookies.get('ref'))
    }
  } else {
    refAddress = getDefaultAddress()
  }

  const onTokenSwap = useCallback(
    async (chadAddress, tokenAddress, chainId, tokenSymbol) => {
      log('debug', chainId, currentChainId)
      // Check if the current network is the same as the token's network (chainId)
      if (chainId !== currentChainId) {
        log(
          `Network mismatch: Switching network from ${currentChainId} to ${chainId}`
        )
        switchChain({ chainId: chainId }) // Switch to the token's network
        if (switchError) {
          throw new Error(
            `Failed to switch to the correct network: ${switchError.message}`
          )
        }
      }
      setPoolAddress(chadAddress)
      try {
        setCreating1(true)
        let swap
        let sendData
        swap = await writeContract(wagmiAdapter.wagmiConfig, {
          address: chadAddress,
          abi: ChadAbi,
          functionName: 'buyToken',
          value: Math.floor((buyAmount * 10 ** 18) / etherPrice[chainId]),
          args: [refAddress, 0],
          account: address,
          gas: 7000000,
          chainId: chainId
        })
        await waitForTransactionReceipt(wagmiAdapter.wagmiConfig, {
          hash: swap
        })
        sendData = {
          chainId,
          buyer: address,
          type: 'bought',
          name: tokenSymbol,
          amount: Math.floor(buyAmount / etherPrice[chainId]),
          token: tokenAddress,
          contract: chadAddress,
          timestamp: (Date.now() / 1000).toFixed(0)
        }
        if (sendData) {
          const response = await fetch(apiUrl + '/api/addHistory', {
            method: 'POST',
            mode: 'cors',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            redirect: 'error',
            body: JSON.stringify(sendData)
          })
          if (response.status !== 200) {
            const { error } = await response.json()
            throw new Error(error)
          }
        }
        setTimeout(function () {
          setCreating1(false)
        }, 5000)
        toast.success(
          `Successfully ${Number(
            buyAmount / etherPrice[chainId]
          ).toLocaleString()} ${coinNames[chainId]} swapped`
        )
      } catch (err) {
        warn('swap error', err)
        if (address === undefined) {
          toast.error('Connect wallet')
        } else {
          toast.error(
            'There is a problem with your Swap. Please try again later'
          )
        }
        setCreating1(false)
      }
    },
    [
      currentChainId,
      switchChain,
      switchError,
      buyAmount,
      etherPrice,
      refAddress,
      address
    ]
  )

  // Add above the grid:
  const areEqualGrid = (prev, next) => {
    if (prev.buyKey !== next.buyKey) return false
    if (prev.watchlistKey !== next.watchlistKey) return false

    const a = prev.items
    const b = next.items
    if (a === b) return true
    if (!a || !b) return false
    if (a.length !== b.length) return false

    for (let i = 0; i < a.length; i++) {
      const ka = `${a[i]?.contractAddress}:${a[i]?.chainId}`
      const kb = `${b[i]?.contractAddress}:${b[i]?.chainId}`
      if (ka !== kb) return false
    }
    return true
  }

  const LaunchpadCardGrid = memo(function LaunchpadCardGrid({
    items,
    watchlist,
    // optional flags/keys passed from parent; prefixed to avoid unused warnings
    phase2FinalDone,
    buyKey: _buyKey,
    watchlistKey: _watchlistKey,
    loadingEssential,
    loadingDeferred,
    firstPaintDone
  }) {
    // Deduplicate by contract+chain to avoid React key collisions
    const uniqueItems = useMemo(() => {
      const out = []
      const seen = new Set()
      for (const it of items || []) {
        const key = `${it.contractAddress}:${it.chainId}`
        if (seen.has(key)) continue
        seen.add(key)
        out.push(it)
      }
      return out
    }, [items])

    // Lightweight grid skeleton to avoid blank space during initial Phase-1
    const shouldShowSkeleton = !firstPaintDone

    if (shouldShowSkeleton) {
      const skeletons = Array.from({ length: 9 })
      return (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {skeletons.map((_, i) => (
            <div
              key={i}
              className="bg-[#171a21] rounded-lg p-3 flex flex-col gap-3 animate-pulse min-h-[180px]"
            >
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-gray-700" />
                <div className="flex-1">
                  <div className="h-4 w-28 bg-gray-700 rounded mb-2" />
                  <div className="h-3 w-20 bg-gray-700 rounded" />
                </div>
                <div className="h-5 w-5 bg-gray-700 rounded" />
              </div>

              <div className="h-28 w-full bg-gray-800 rounded-md" />

              <div className="flex items-center justify-between">
                <div className="h-4 w-16 bg-gray-700 rounded" />
                <div className="h-4 w-14 bg-gray-700 rounded" />
                <div className="h-4 w-12 bg-gray-700 rounded" />
              </div>
            </div>
          ))}
        </div>
      )
    }

    return (
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {uniqueItems.map(
          ({
            startTime,
            chainId,
            progress,
            liquidity,
            tokenName,
            tokenSymbol,
            tokenDescription,
            logoUrl,
            bannerUrl,
            depositedAmount,
            contractAddress,
            tokenAddress,
            devAddress,
            dexName,
            marketCap,
            website,
            twitter,
            telegram,
            blockchainLogoUrl,
            lpCreated,
            lastAction,
            lastActionNumber,
            top15HoldersPercent,
            devPercent,
            holdersNumber,
            tokenId,
            versionType,
            canCreate
          }) => (
            <MemoLaunchpadCard
              key={`${contractAddress}:${chainId}`}
              apiUrl={apiUrl}
              coinNames={coinNames}
              web3Clients={web3Clients}
              buyAmount={buyPerChain[chainId] || 0}
              startTime={startTime}
              chainId={chainId}
              progress={progress}
              liquidity={liquidity}
              tokenName={tokenName}
              tokenSymbol={tokenSymbol}
              tokenDescription={tokenDescription}
              description={tokenDescription}
              Banner={bannerUrl}
              logoUrl={logoUrl}
              blockchainLogoUrl={blockchainLogoUrl}
              chadAddress={contractAddress}
              depositedAmount={depositedAmount}
              contractAddress={contractAddress}
              tokenAddress={tokenAddress}
              devAddress={devAddress}
              dexName={dexName}
              marketCap={marketCap}
              website={website}
              twitter={twitter}
              telegram={telegram}
              lpCreated={lpCreated}
              watchlist={watchlist}
              toggleWatchlist={toggleWatchlist}
              lastAction={lastAction}
              lastActionNumber={lastActionNumber}
              top15HoldersPercent={top15HoldersPercent}
              devPercent={devPercent}
              holdersNumber={holdersNumber}
              recentBuzz={isAddrHot(contractAddress)}
              tokenId={tokenId}
              versionType={versionType}
              canCreate={canCreate}
            />
          )
        )}
      </div>
    )
  }, areEqualGrid)

  // Modal Section
  const toggleModal = () => {
    setModalIsOpen(!modalIsOpen)
    cookies.set('show-how-it-works', 'true')
  }

  const closeModal = e => {
    if (e.target.id === 'modal') {
      setModalIsOpen(false)
      cookies.set('show-how-it-works', 'true')
    }
  }

  const modalContent = (
    <div
      id="modal"
      onClick={closeModal}
      className={`modal ${modalIsOpen ? 'show-modal' : ''}`}
    >
      <div className="bg-[#f3f3f3] rounded-[25px] max-w-[480px] p-[24px]">
        <h1 className="text-center text-xl pb-2 font-extrabold">
          All created tokens in hotcurves.fun are safe!
        </h1>
        <p className="text-[#222]">
          ✅Each coin is a fair-launch with no presale and no team allocation.
        </p>
        <p className="text-[#222]">✅Everyone has equal chance!</p>
        <h1 className="text-center text-xl pb-2 font-extrabold">
          How it works
        </h1>
        <p className="text-[#222]">1️⃣ pick a coin that you like</p>
        <p className="text-[#222]">2️⃣ buy the coin on the bonding curve</p>
        <p className="text-[#222]">
          3️⃣ sell at any time to lock in your profits or losses
        </p>
        <p className="text-[#222]">
          4️⃣ when enough people buy on the bonding curve it reaches a market cap
          of $69k and the token will be listed with $12k of liquidity deposited
          in the preferred DEX.
        </p>
        <p className="text-[#222] mb-4">5️⃣LP Tokens will be burned🔥</p>
        <p className="text-[#222] italic text-[14px]">Disclaimer:</p>
        <p className="text-[#222] italic text-[14px]">
          Trading tokens is highly risky / speculative. Do not invest more than
          you can afford to lose. Anyone can list a token, listing does not mean
          we endorse the token. Token prices can be extremely volatile. Be sure
          to follow any legal guidelines that your country specifies.
        </p>
        <div className="text-center mt-4">
          <button
            onClick={toggleModal}
            className="rounded-full bg-[#2f6434] px-4 py-3 text-white"
          >
            I'm ready to pump
          </button>
        </div>
      </div>
    </div>
  )

  const SkeletonCard = () => (
    <div
      className="group rounded-md border border-[#2a2d36] bg-[#171a21] text-[#f8ffe8] px-2 md:pl-2 md:pr-3 py-2.5 relative overflow-hidden animate-pulse"
      aria-busy="true"
      aria-live="polite"
    >
      <div className="flex gap-3">
        {/* Left: cover image */}
        <div className="relative shrink-0 w-[84px] h-[84px] rounded-lg bg-white/10 overflow-hidden">
          {/* chain badge placeholder */}
          <div className="absolute bottom-1 right-1 h-6 w-6 md:h-7 md:w-7 rounded-full bg-white/20 border border-white/10" />
        </div>

        {/* Right: main content */}
        <div className="flex-1 min-w-0">
          {/* Header: ticker pill + name, and right controls */}
          <div className="flex items-start gap-2">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                {/* ticker pill */}
                <div className="h-5 w-16 rounded-[6px] bg-[#f500ff]/20" />
                {/* name (single-line) */}
                <div className="h-4 w-[40%] rounded bg-white/10" />
              </div>
              {/* description – fixed two lines */}
              <div className="mt-1 h-[36px] leading-[18px]">
                <div className="h-[8px] w-[85%] bg-white/10 rounded" />
                <div className="mt-2 h-[8px] w-[60%] bg-white/10 rounded" />
              </div>
            </div>
            {/* Right header controls (last-action pill + buy icon) */}
            <div className="flex items-start gap-2 ml-1.5">
              <div className="h-5 w-10 rounded bg-white/10" />
              <div className="h-6 w-6 rounded-lg bg-white/10" />
            </div>
          </div>

          {/* Divider */}
          <div className="mt-2 h-px w-full bg-white/5" />

          {/* Footer: two lines × two columns */}
          <div className="grid grid-cols-[1.15fr_0.85fr] gap-y-1 items-start content-center w-full pt-1">
            {/* Row 1, Col 1: badges with right-edge fade */}
            <div className="relative self-center min-w-0">
              <div className="flex gap-1 items-center whitespace-nowrap overflow-hidden justify-start pr-2">
                <div className="h-[18px] w-[42px] rounded-[6px] border border-white/10 bg-white/5" />
                <div className="h-[18px] w-[56px] rounded-[6px] border border-white/10 bg-white/5" />
                <div className="h-[18px] w-[52px] rounded-[6px] border border-white/10 bg-white/5" />
                <div className="h-[18px] w-[46px] rounded-[6px] border border-white/10 bg-white/5" />
              </div>
              <div className="pointer-events-none absolute inset-y-0 right-0 w-4 bg-gradient-to-l from-[#171a21] to-transparent" />
            </div>

            {/* Row 1, Col 2: numbers (volume & market cap) */}
            <div className="grid grid-cols-2 gap-0 justify-items-end self-center -mr-1">
              <div className="h-4 w-[88px] bg-white/10 rounded" />
              <div className="h-4 w-[88px] bg-white/10 rounded" />
            </div>

            {/* Row 2, Col 1: socials + holders + star */}
            <div className="flex items-center gap-2 self-center">
              <div className="w-[12px] h-[12px] rounded bg-white/10" />
              <div className="w-[12px] h-[12px] rounded bg-white/10" />
              <div className="w-[12px] h-[12px] rounded bg-white/10" />
              <div className="h-3 w-10 bg-white/10 rounded" />
              <div className="w-6 h-6 rounded bg-white/10" />
            </div>

            {/* Row 2, Col 2: labels under numbers (mirrored widths) */}
            <div className="grid grid-cols-2 gap-0 justify-items-end self-center -mr-1">
              <div className="h-2 w-[88px] bg-white/10 rounded" />
              <div className="h-2 w-[88px] bg-white/10 rounded" />
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div>
      <style>{`
      .modal {
        opacity: 0;
        visibility: hidden;
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        transition: all 0.3s ease;
        z-index: 10;
      }
      .show-modal {
        opacity: 1;
        visibility: visible;
      }
    `}</style>
      {modalContent}
      <div className="GlobalContainer launches-all-padding">
        <div>
          <TopBar />
          <div className="max-w-[90rem] m-auto pt-12 pb-8 px-2 sm:px-4 sm:py-10 relative z-[1]">
            {/* spacer to offset the sticky ticker/topbar on small screens */}
            <div className="h-20 sm:h-0" aria-hidden="true" />
            <RevenueHeader
              accumFee={accumFee}
              accumFeeUnavailable={accumFeeUnavailable}
              firstPaintDone={firstPaintDone}
              phase2FinalDone={phase2FinalDone}
            />
            {/* Apply mt-2 to overall group and space-y-2 to space children evenly */}
            <div className="mt-2 space-y-2">
              {/* Top 10 + Graduated section */}
              <div className="grid md:grid-cols-[2fr_1fr] gap-3 md:gap-2 items-start">
                <section className="min-w-0">
                  <TopTenList
                    showTop10={showTop10}
                    loadingEssential={loadingEssential}
                    loadingDeferred={loadingDeferred}
                    timeFilterChadLists={timeFilterChadLists}
                    chadLists={chadLists}
                    totalVol={totalVol}
                    totalMc={totalMc}
                    watchlist={watchlist}
                    toggleWatchlist={toggleWatchlist}
                    onTokenSwap={onTokenSwap}
                    creating1={creating1}
                    poolAddress={poolAddress}
                    firstPaintDone={firstPaintDone}
                    selectedItem={selectedItem}
                    setSelectedItem={setSelectedItem}
                  />
                </section>
                <aside className="block md:sticky md:top-3">
                  <GraduatedCoins
                    graduatedPaintDone={graduatedPaintDone}
                    gradSkelHoldDone={gradSkelHoldDone}
                    graduatedItems={graduatedItems}
                    graduatedByFees={graduatedByFees}
                    gradVisibleCount={gradVisibleCount}
                    setGradVisibleCount={setGradVisibleCount}
                    GRAD_PAGE_SIZE={GRAD_PAGE_SIZE}
                    toggleWatchlist={toggleWatchlist}
                    watchlist={watchlist}
                  />
                </aside>
              </div>

              {/* Featured Projects */}
              {chadLists.filter(item => item.featureTime > Date.now() / 1000)
                .length > 0 && (
                <div>
                  <div className="text-[#f3f3f3] text-xl">
                    Featured Projects
                  </div>
                  <Suspense fallback={null}>
                    <Slider
                      items={chadLists.filter(
                        item => item.featureTime > Date.now() / 1000
                      )}
                      isListed={false}
                    />
                  </Suspense>
                </div>
              )}

              {/* Search & Filters */}
              <SearchSection
                showMobileFilters={showMobileFilters}
                setShowMobileFilters={setShowMobileFilters}
                searchInput={searchInput}
                onSearchChanged={onSearchChanged}
                FilterSelect={FilterSelect}
                sortOptions={sortOptions}
                sortValue={sortValue}
                onSortChange={onSortChange}
                statusOptions={statusOptions}
                statusValue={statusValue}
                onStatusChange={onStatusChange}
                chainOptions={chainOptions}
                chainValue={chainValue}
                onChainChange={onChainChange}
                buyAmount={buyAmount}
                setBuyAmount={setBuyAmount}
                onResetFilters={onResetFilters}
                orderValue={orderValue}
                onOrderChange={onOrderChange}
                liveBuzzEnabled={liveBuzzEnabled}
                setLiveBuzzEnabled={setLiveBuzzEnabled}
              />

              {/* Main Grid or Skeleton */}
              {loadingEssential ? (
                <div
                  className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
                  style={{ contentVisibility: 'auto' }}
                >
                  {Array.from({ length: 6 }).map((_, i) => (
                    <SkeletonCard key={i} />
                  ))}
                </div>
              ) : sortedChadLists.length > 0 ? (
                <>
                  <LaunchpadCardGrid
                    items={reorderByRecentBumps(sortedChadLists).slice(
                      0,
                      visibleCount
                    )}
                    watchlist={watchlist}
                    watchlistKey={watchlist.join(',')}
                    buyKey={`${buyAmount}-${Object.values(
                      etherPrice || {}
                    ).join(',')}`}
                    phase2FinalDone={phase2FinalDone}
                    loadingEssential={loadingEssential}
                    loadingDeferred={loadingDeferred}
                    firstPaintDone={firstPaintDone}
                  />
                  <div ref={loadMoreRef} className="h-8" />
                  {visibleCount < sortedChadLists.length && (
                    <div className="h-6 flex items-center justify-center text-gray-400 text-xs">
                      {/* Auto-loading more... */}
                    </div>
                  )}
                </>
              ) : (
                <div className="loadingBox">
                  <p className="Text1" style={{ color: 'white' }}>
                    no tokens match your search. try adjusting the filters or
                    search keyword.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Suspense fallback={null}>
        <Footer />
      </Suspense>
    </div>
  )
}

export default App
